import React, { useEffect, useRef, useState } from 'react';
import { Button, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import JsBarcode from 'jsbarcode';
import html2canvas from 'html2canvas';
import { getBusinessProfile } from '../../../services/SettingsService';
import { getTrackingSettings } from '../../../services/TrackingService';
import { updateShippingLabel, getOrderTrackingLink } from '../../../services/OrdersService';

// Standard shipping label size at print resolution (default 100x15mm, admin-configurable
// — see DEFAULT_LABEL_SETTINGS below) at 300dpi. CSS reference pixel = 96dpi, so
// scale = target-dpi / 96 upsizes the html2canvas capture to the requested print
// resolution regardless of the label's on-screen size or configured mm dimensions.
const PRINT_SCALE = 300 / 96;
const MAX_BOXES = 50;

// JsBarcode's own default bar (module) width — always rendered at this fixed size,
// regardless of how long the encoded value is. Overflow is prevented purely by CSS
// (max-width: 100% on the barcode <img>, see render below), not by adjusting this number.
const BARCODE_BAR_WIDTH = 3;

// Matches the label's original hardcoded values — nothing changes visually until an
// admin adjusts one of these via Settings > Tracking > Label settings.
const DEFAULT_LABEL_SETTINGS = {
    label_width_mm: 100,
    label_height_mm: 150,
    label_header_font_size: 16,
    label_address_font_size: 13,
    label_info_font_size: 15,
    label_barcode_caption_font_size: 16,
    label_to_font_size: 18,
    label_show_from: true,
    label_from_name_on: true,
    label_from_name_text: 'Tabliya B.V.',
    label_from_address_on: false,
    label_from_address_text: 'Disketteweg 14, 1033 NW',
    label_from_email_on: true,
    label_from_email_text: 'info@tabliya.nl',
};

// Toggles/texts must not use `||` fallbacks like the numeric fields (false and '' are
// legitimate saved values), so they fall back only when the key is absent.
const pick = (data, key) => (data[key] === undefined || data[key] === null ? DEFAULT_LABEL_SETTINGS[key] : data[key]);

export default function ShippingLabel({ order, onOrderUpdate }) {
    const { t, i18n } = useTranslation();
    const labelRefs = useRef([]);
    const barcodeRefs = useRef([]);
    const [profile, setProfile] = useState(null);
    const [prefix, setPrefix] = useState('TAB');
    const [labelSettings, setLabelSettings] = useState(DEFAULT_LABEL_SETTINGS);
    const [splitting, setSplitting] = useState(false);
    const [copyingKey, setCopyingKey] = useState(null);

    useEffect(() => {
        getBusinessProfile().then((res) => setProfile(res.data)).catch(() => setProfile(null));
        // Tracking settings live behind the `tracking` permission — an orders-only admin
        // may not have it. Fall back to defaults rather than blocking the label, same
        // pattern as the other settings-backed defaults in the order screens.
        getTrackingSettings()
            .then((res) => {
                setPrefix(res.data.tracking_number_prefix || 'TAB');
                setLabelSettings({
                    label_width_mm: res.data.label_width_mm || DEFAULT_LABEL_SETTINGS.label_width_mm,
                    label_height_mm: res.data.label_height_mm || DEFAULT_LABEL_SETTINGS.label_height_mm,
                    label_header_font_size: res.data.label_header_font_size || DEFAULT_LABEL_SETTINGS.label_header_font_size,
                    label_address_font_size: res.data.label_address_font_size || DEFAULT_LABEL_SETTINGS.label_address_font_size,
                    label_info_font_size: res.data.label_info_font_size || DEFAULT_LABEL_SETTINGS.label_info_font_size,
                    label_barcode_caption_font_size:
                        res.data.label_barcode_caption_font_size || DEFAULT_LABEL_SETTINGS.label_barcode_caption_font_size,
                    label_to_font_size: res.data.label_to_font_size || DEFAULT_LABEL_SETTINGS.label_to_font_size,
                    label_show_from: !!pick(res.data, 'label_show_from'),
                    label_from_name_on: !!pick(res.data, 'label_from_name_on'),
                    label_from_name_text: pick(res.data, 'label_from_name_text'),
                    label_from_address_on: !!pick(res.data, 'label_from_address_on'),
                    label_from_address_text: pick(res.data, 'label_from_address_text'),
                    label_from_email_on: !!pick(res.data, 'label_from_email_on'),
                    label_from_email_text: pick(res.data, 'label_from_email_text'),
                });
            })
            .catch(() => { setPrefix('TAB'); setLabelSettings(DEFAULT_LABEL_SETTINGS); });
    }, []);

    // Derived values must tolerate `order` being null/undefined — hooks can't be called
    // conditionally, so the "no order yet" early return has to come after every hook.
    const boxCount = Math.max(1, Number(order?.shipping_box_count) || 1);
    const trackingNumber = order ? `${prefix}${String(order.id).padStart(6, '0')}` : '';
    const boxes = Array.from({ length: boxCount }, (_, i) => i + 1);
    const { label_width_mm: widthMm, label_height_mm: heightMm } = labelSettings;
    // Font-size settings are defined for the 100x150mm reference label; other sizes scale
    // them (and the logo/barcode, which already follow the height) by the tighter
    // dimension so a smaller label does not overflow onto a second page. Exactly 1 at the default.
    const scale = Math.max(0.5, Math.min(2, Math.min(widthMm / 100, heightMm / 150)));
    const fs = (px) => Math.round(px * scale * 10) / 10;

    useEffect(() => {
        if (!order) return;
        boxes.forEach((boxNum, idx) => {
            const el = barcodeRefs.current[idx];
            if (!el) return;
            const value = boxCount > 1 ? `${trackingNumber}-${boxNum}` : trackingNumber;
            // Always rendered at the same fixed size regardless of value length — overflow
            // is prevented entirely by CSS (max-width: 100% on the barcode <img>, see JSX
            // below), not by measuring anything or adjusting these options per-value.
            // Target is a plain <img>, not an <svg> — JsBarcode auto-detects this and
            // renders to an internal canvas, then sets img.src to the resulting data URL.
            // html2canvas (used by the Download button) has a known limitation with inline
            // <svg> elements ignoring CSS-computed sizing on export — it uses the SVG's own
            // intrinsic width/height attributes instead of the max-width-constrained
            // rendered box, so a CSS-shrunk <svg> still exported at full, overflowing size.
            // A plain raster <img> doesn't have that problem — image scaling is one of
            // html2canvas's most reliably correct code paths.
            JsBarcode(el, value, {
                format: 'CODE128',
                // Caption is rendered as HTML under the image (see JSX) so the barcode can be
                // stretched to the full label width without distorting the text.
                displayValue: false,
                height: 100,
                margin: 10,
                width: BARCODE_BAR_WIDTH,
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackingNumber, boxCount]);

    if (!order) return null;

    // Product lines only: the delivery/shipping line is not a physical item in the box.
    const itemCount = (order.items || [])
        .filter((it) => !it.is_delivery && (it.line_type || 'product') !== 'shipping')
        .reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
    // Same fallback the invoice already uses for "no logo uploaded" (OrderInvoice.js) — a
    // bundled static asset, not text. Keeping both components' fallback identical avoids
    // the label looking inconsistent with the invoice right next to it.
    const logoSrc = profile?.logo_base64 || `${window.location.origin}${process.env.PUBLIC_URL || ''}/logo-notagline-light.png`;
    const fromLines = labelSettings.label_show_from
        ? [
            labelSettings.label_from_name_on && labelSettings.label_from_name_text,
            labelSettings.label_from_address_on && labelSettings.label_from_address_text,
            labelSettings.label_from_email_on && labelSettings.label_from_email_text,
        ].filter((line) => typeof line === 'string' && line.trim())
        : [];
    // Postcode + city on one line (postcode only when there is no city), then the country
    // name in the UI language, derived from the stored ISO code. Missing parts are skipped.
    const postcodeCity = [order.zip_code, order.city].map((x) => (x || '').trim()).filter(Boolean).join(' ');
    const countryCode = (order.country_code || '').trim().toUpperCase();
    let countryName = '';
    if (countryCode) {
        try {
            countryName = new Intl.DisplayNames([i18n?.language || 'en'], { type: 'region' }).of(countryCode) || countryCode;
        } catch {
            countryName = countryCode;
        }
    }
    const deliveryAddress = [order.street_name, order.house_number].filter(Boolean).join(' ');

    const handleSplit = async () => {
        const { value } = await Swal.fire({
            title: t('shippingLabel.splitPromptTitle'),
            input: 'number',
            inputAttrs: { min: 2, max: MAX_BOXES },
            inputValue: 2,
            showCancelButton: true,
            confirmButtonText: t('common.save'),
            cancelButtonText: t('shippingLabel.cancel'),
        });
        const n = parseInt(value, 10);
        if (!n || n < 2 || n > MAX_BOXES) return;
        await saveBoxCount(n);
    };

    const handleReset = async () => {
        await saveBoxCount(1);
    };

    const saveBoxCount = async (n) => {
        setSplitting(true);
        try {
            const res = await updateShippingLabel(order.id, { box_count: n });
            onOrderUpdate?.(res.data);
        } catch {
            toast.error(t('shippingLabel.errors.save'));
        } finally {
            setSplitting(false);
        }
    };

    const handleCopyLink = async (boxNum) => {
        const key = boxNum || 'single';
        setCopyingKey(key);
        try {
            const res = await getOrderTrackingLink(order.id, boxCount > 1 ? boxNum : undefined);
            await navigator.clipboard.writeText(res.data.tracking_link);
            toast.success(t('shippingLabel.linkCopied'));
        } catch {
            toast.error(t('shippingLabel.errors.copyLink'));
        } finally {
            setCopyingKey(null);
        }
    };

    const downloadBox = async (idx) => {
        const node = labelRefs.current[idx];
        if (!node) return;
        const canvas = await html2canvas(node, { scale: PRINT_SCALE, backgroundColor: '#ffffff' });
        const link = document.createElement('a');
        const suffix = boxCount > 1 ? `-box${idx + 1}of${boxCount}` : '';
        link.download = `label-${order.order_number || order.id}${suffix}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const handleDownloadAll = async () => {
        for (let i = 0; i < boxes.length; i++) {
            // eslint-disable-next-line no-await-in-loop
            await downloadBox(i);
        }
    };

    const printNodes = (nodes) => {
        const printWindow = window.open('', '_blank', 'width=500,height=700');
        if (!printWindow) { alert(t('pdf.popupBlocked')); return; }

        // This popup starts with a totally bare document — none of the app's stylesheets
        // are linked into it, so every Bootstrap/utility class the label relies on for its
        // actual appearance (p-3 padding, border, text-center, badge styling, the
        // border-bottom dividers — all of it) silently did nothing here, regardless of what
        // renders correctly on-screen. Cloning the app's own <link>/<style> tags in fixes
        // that for every element at once, not just the barcode.
        const clonedStyleTags = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
            .map((el) => {
                if (el.tagName === 'LINK') {
                    const absoluteHref = new URL(el.getAttribute('href'), window.location.href).href;
                    return `<link rel="stylesheet" href="${absoluteHref}">`;
                }
                return el.outerHTML;
            })
            .join('\n');

        printWindow.document.write(`<!DOCTYPE html><html><head>
            <title>${t('shippingLabel.heading')}</title>
            ${clonedStyleTags}
            <style>
                @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
                body { margin: 0; padding: 0; background: #fff; font-family: Arial, sans-serif; }
                .shipping-label { width: ${widthMm}mm; height: ${heightMm}mm; box-sizing: border-box; page-break-after: always; }
                .shipping-label:last-child { page-break-after: auto; }
                img { max-width: 100%; }
            </style></head><body></body></html>`);
        printWindow.document.close();

        nodes.forEach((node) => {
            if (!node) return;
            const imported = printWindow.document.importNode(node, true);
            printWindow.document.body.appendChild(imported);
        });

        // Cloned <link> stylesheets load asynchronously — printing before they're applied
        // would reproduce the exact unstyled-popup bug this is fixing. Wait for all of them
        // (or a fallback timeout, so a slow/failed stylesheet load can't block printing
        // forever) before invoking the browser's print dialog.
        const linkEls = Array.from(printWindow.document.querySelectorAll('link[rel="stylesheet"]'));
        const stylesReady = Promise.all(linkEls.map((link) => new Promise((resolve) => {
            link.addEventListener('load', resolve, { once: true });
            link.addEventListener('error', resolve, { once: true });
        })));
        const fallbackTimeout = new Promise((resolve) => { setTimeout(resolve, 1500); });

        printWindow.onload = () => {
            Promise.race([stylesReady, fallbackTimeout]).then(() => {
                setTimeout(() => { printWindow.print(); }, 100);
            });
        };
    };

    const handlePrintBox = (idx) => printNodes([labelRefs.current[idx]]);
    const handlePrintAll = () => printNodes(labelRefs.current.slice(0, boxCount));

    return (
        <div>
            <div className="mb-3 no-print d-flex align-items-center gap-2 flex-wrap">
                <Button variant="outline-secondary" size="sm" onClick={handleSplit} disabled={splitting}>
                    <i className="fa-solid fa-boxes-stacked me-1" /> {t('shippingLabel.split')}
                </Button>
                {boxCount > 1 && (
                    <Button variant="outline-danger" size="sm" onClick={handleReset} disabled={splitting}>
                        <i className="fa-solid fa-rotate-left me-1" /> {t('shippingLabel.reset')}
                    </Button>
                )}
                <Button variant="outline-secondary" size="sm" onClick={handleDownloadAll}>
                    <i className="fa-solid fa-image me-1" /> {boxCount > 1 ? t('shippingLabel.downloadAll') : t('shippingLabel.downloadImage')}
                </Button>
                <Button variant="outline-primary" size="sm" onClick={handlePrintAll}>
                    <i className="fa-solid fa-print me-1" /> {boxCount > 1 ? t('shippingLabel.printAll') : t('shippingLabel.print')}
                </Button>
                {boxCount === 1 && (
                    <Button variant="outline-secondary" size="sm" onClick={() => handleCopyLink(null)} disabled={copyingKey === 'single'}>
                        <i className="fa-solid fa-link me-1" /> {t('shippingLabel.copyLink')}
                    </Button>
                )}
            </div>

            <div className="d-flex flex-wrap gap-3">
                {boxes.map((boxNum, idx) => (
                    <div key={boxNum}>
                        {boxCount > 1 && (
                            <div className="mb-2 no-print d-flex align-items-center gap-2 flex-wrap">
                                <span className="text-muted small">{t('shippingLabel.box')} {boxNum} {t('shippingLabel.of')} {boxCount}</span>
                                <Button variant="link" size="sm" className="p-0" onClick={() => downloadBox(idx)} title={t('shippingLabel.downloadImage')}>
                                    <i className="fa-solid fa-download" />
                                </Button>
                                <Button variant="link" size="sm" className="p-0" onClick={() => handlePrintBox(idx)} title={t('shippingLabel.print')}>
                                    <i className="fa-solid fa-print" />
                                </Button>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    onClick={() => handleCopyLink(boxNum)}
                                    disabled={copyingKey === boxNum}
                                    title={t('shippingLabel.copyLink')}
                                >
                                    <i className="fa-solid fa-link" />
                                </Button>
                            </div>
                        )}
                        <div
                            ref={(el) => { labelRefs.current[idx] = el; }}
                            className="shipping-label border p-3 bg-white d-flex flex-column"
                            style={{
                                width: `${widthMm}mm`,
                                minHeight: `${heightMm}mm`,
                                color: '#1a252f',
                                fontSize: fs(labelSettings.label_address_font_size),
                                boxSizing: 'border-box',
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                                <img
                                    src={logoSrc}
                                    alt={profile?.business_name || 'Logo'}
                                    style={{ height: `${heightMm * 0.075}mm`, maxWidth: '55%', objectFit: 'contain' }}
                                />
                                <Badge bg="info" text="dark" style={{ fontSize: fs(labelSettings.label_header_font_size) }}>
                                    <i className="fa-solid fa-snowflake me-1" /> {t('shippingLabel.keepFrozen')}
                                </Badge>
                            </div>

                            {fromLines.length > 0 && (
                                <div className="mb-2 pb-2 border-bottom" style={{ fontSize: fs(labelSettings.label_address_font_size) }}>
                                    <div className="text-muted small">{t('shippingLabel.from')}</div>
                                    {fromLines.map((line) => <div key={line}>{line}</div>)}
                                </div>
                            )}

                            <div
                                className="mb-2 pb-2 border-bottom d-flex flex-column justify-content-center flex-grow-1"
                                style={{ fontSize: fs(labelSettings.label_to_font_size), lineHeight: 1.3 }}
                            >
                                <div className="text-muted small">{t('shippingLabel.to')}</div>
                                <div style={{ fontSize: '1.3em', fontWeight: 700, lineHeight: 1.2 }}>{order.buyer_name}</div>
                                <div>{deliveryAddress}</div>
                                {postcodeCity && <div>{postcodeCity}</div>}
                                {countryName && <div>{countryName}</div>}
                            </div>

                            <div
                                className="mb-2 pb-2 border-bottom d-flex flex-wrap gap-3"
                                style={{ fontSize: fs(labelSettings.label_info_font_size) }}
                            >
                                <div>{t('shippingLabel.orderNumber')} {order.order_number}</div>
                                <div>{t('shippingLabel.orderDate')}: {order.order_date ? new Date(order.order_date).toLocaleDateString() : '-'}</div>
                                <div>{t('shippingLabel.items')}: {itemCount}</div>
                                {boxCount > 1 && <div>{t('shippingLabel.box')}: {boxNum} {t('shippingLabel.of')} {boxCount}</div>}
                            </div>

                            <div className="text-center mt-1">
                                <img
                                    ref={(el) => { barcodeRefs.current[idx] = el; }}
                                    alt={boxCount > 1 ? `${trackingNumber}-${boxNum}` : trackingNumber}
                                    style={{ display: 'block', width: '100%', height: `${heightMm * 0.16}mm`, objectFit: 'fill' }}
                                />
                                <div style={{ fontSize: fs(labelSettings.label_barcode_caption_font_size), fontWeight: 600, letterSpacing: '0.08em' }}>
                                    {boxCount > 1 ? `${trackingNumber}-${boxNum}` : trackingNumber}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
